import styled, { keyframes } from 'styled-components';
import { Color } from 'core';

interface AvatarProps {
  url: string;
}

interface PageButtonProps {
  active?: boolean;
}

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-20px); }
  to { opacity: 1; transform: translateY(0); }
`;

export const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(2px);
`;

export const Modal = styled.div`
  width: 90%;
  max-width: 800px;
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  animation: ${fadeIn} 0.3s ease;
  display: flex;
  flex-direction: column;
  max-height: 90vh;
`;

export const Header = styled.div`
  padding: 20px 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid ${Color.veryLightGray};
`;

export const Title = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${Color.lightBlack};
  margin: 0;
  display: flex;
  align-items: center;
`;

export const Count = styled.span`
  font-size: 14px;
  font-weight: normal;
  color: ${Color.darkGray};
  margin-left: 8px;
`;

export const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${Color.darkGray};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    background: ${Color.veryLightGray};
    color: ${Color.lightBlack};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${Color.lightGreen}40;
  }
`;

export const SearchWrapper = styled.div`
  margin: 16px 24px;
  position: relative;
  display: flex;
  align-items: center;
  background: ${Color.primaryGray};
  border: 1px solid ${Color.veryLightGray};
  border-radius: 8px;
  transition: all 0.2s;

  &:focus-within {
    border-color: ${Color.lightGreen};
    box-shadow: 0 0 0 3px ${Color.lightGreen}20;
  }
`;

export const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: transparent;
  padding: 12px 0;
  font-size: 15px;
  color: ${Color.lightBlack};

  &::placeholder {
    color: ${Color.darkGray};
  }
`;

export const ClearButton = styled.button`
  background: none;
  border: none;
  padding: 0 12px;
  color: ${Color.darkGray};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: ${Color.lightBlack};
  }
`;

export const Content = styled.div`
  padding: 0 24px 24px; // Added bottom padding
  overflow-y: auto;
  flex: 1;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: ${Color.lightGray};
    border-radius: 3px;
  }
`;

export const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(230px, 1fr));
  gap: 16px;
  margin: 4px;
`;

export const Card = styled.div`
  background: ${Color.primaryGray};
  border-radius: 8px;
  padding: 16px;
  display: grid;
  grid-template-columns: 40px 1fr auto;
  align-items: center;
  gap: 12px;
  cursor: pointer;
  position: relative;
  transition: all 0.2s ease;
  border: 2px solid transparent;

  &:hover {
    background: white;
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.08);
    transform: translateY(-2px);
  }

  &:focus {
    border-color: ${Color.lightGreen};
    box-shadow: 0 0 0 2px ${Color.lightGreen}30;
    background: white;
    transform: translateY(-2px);
    z-index: 1;
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  }
`;

export const PartnerInfo = styled.div`
  display: flex;
  flex-direction: column;
  min-width: 0; // Helps with text overflow
`;

export const Name = styled.div`
  font-size: 14px;
  font-weight: 500;
  color: ${Color.lightBlack};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  line-height: 1.2;
`;

export const PartnerId = styled.div`
  font-size: 12px;
  color: ${Color.darkGray};
  margin-top: 2px;
  font-family: "SF Mono", "Roboto Mono", Consolas, monospace;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${Card}:hover & {
    color: ${Color.lightGreen};
  }
`;

export const Avatar = styled.div<AvatarProps>`
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: #eee url(${(props) => props.url}) no-repeat center/cover;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
  border: 2px solid white;

  ${Card}:hover & {
    box-shadow: 0 3px 8px rgba(0, 0, 0, 0.15);
  }
`;

export const DepartmentIcon = styled.div`
  color: ${Color.lightGreen};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.2s ease;

  ${Card}:hover & {
    transform: scale(1.1);
  }
`;

export const PaginationContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px 24px;
  border-top: 1px solid ${Color.veryLightGray};
`;

export const PaginationButton = styled.button`
  background: none;
  border: none;
  color: ${Color.darkGray};
  width: 32px;
  height: 32px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &:not(:disabled):hover {
    background: ${Color.veryLightGray};
    color: ${Color.lightBlack};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${Color.lightGreen}40;
  }
`;

export const PageButton = styled.button<PageButtonProps>`
  background: ${(props) => (props.active ? Color.lightGreen : 'transparent')};
  border: none;
  color: ${(props) => (props.active ? 'white' : Color.darkGray)};
  width: 32px;
  height: 32px;
  border-radius: 4px;
  margin: 0 4px;
  font-weight: ${(props) => (props.active ? '600' : '400')};
  cursor: pointer;
  transition: all 0.2s;

  &:not(:disabled):hover {
    background: ${(props) => (props.active ? Color.lightGreen : Color.veryLightGray)};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px ${Color.lightGreen}40;
  }
`;

export const Ellipsis = styled.span`
  color: ${Color.darkGray};
  margin: 0 4px;
`;

export const EmptyStateContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 48px 24px;
  color: ${Color.darkGray};
  text-align: center;
`;

export const EmptyStateTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  margin: 16px 0 8px;
  color: ${Color.lightBlack};
`;

export const EmptyStateText = styled.p`
  font-size: 14px;
  margin: 0;
`;

export const LoadingState = styled.div`
  padding: 48px 0;
  display: flex;
  justify-content: center;
  align-items: center;

  &:after {
    content: '';
    width: 32px;
    height: 32px;
    border: 3px solid ${Color.veryLightGray};
    border-top-color: ${Color.lightGreen};
    border-radius: 50%;
    animation: spinner 0.8s linear infinite;
  }

  @keyframes spinner {
    to {
      transform: rotate(360deg);
    }
  }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

export const SearchIcon = styled.div`
  padding: 0 12px;
  color: ${Color.darkGray};

  .spin-animation {
    animation: ${spin} 1s linear infinite;
  }
`;
